import React, { useEffect, useRef, useState } from "react";
import { Button, SideSheet, Typography } from "@douyinfe/semi-ui";
import { IconUserCircle } from "@douyinfe/semi-icons";
import { ChatCustomer } from "../../api/aiCoachChat";
import CustomerListPanel from "./components/CustomerListPanel";
import CustomerProfilePanel from "./components/CustomerProfilePanel";
import ChatPanel, { ChatPanelHandle } from "./components/ChatPanel";
import {
  brandHeader,
  mainRow,
  pageWrap,
  sidebarWrap,
  profileCard,
  sp,
} from "./styles";

const { Title, Text } = Typography;

const WIDE_BREAKPOINT = 1600;

function useWide(): boolean {
  const [wide, setWide] = useState(() =>
    typeof window === "undefined" ? true : window.innerWidth >= WIDE_BREAKPOINT
  );
  useEffect(() => {
    const onResize = () => setWide(window.innerWidth >= WIDE_BREAKPOINT);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return wide;
}

const AiCoachChatPage: React.FC = () => {
  const [selected, setSelected] = useState<ChatCustomer | null>(null);
  const [profileOpen, setProfileOpen] = useState(false);
  const wide = useWide();
  const chatRef = useRef<ChatPanelHandle>(null);

  const quickSend = (content: string) => {
    chatRef.current?.send(content);
    setProfileOpen(false);
  };

  return (
    <div style={pageWrap}>
      <div style={brandHeader}>
        <div style={{ display: "flex", alignItems: "center", gap: sp.md }}>
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              background: "linear-gradient(135deg, #6C5CE7 0%, #8E7BFF 100%)",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 20,
              boxShadow: "0 4px 12px rgba(108,92,231,0.35)",
            }}
          >
            ✦
          </div>
          <div>
            <Title heading={5} style={{ margin: 0 }}>AI 军师</Title>
            <Text type="tertiary" size="small">
              陪你跟进每一位客户，随时提问、随时复盘
            </Text>
          </div>
        </div>
        {selected && (
          <div style={{ display: "flex", alignItems: "center", gap: sp.md }}>
            <div style={{ textAlign: "right" }}>
              <Text type="tertiary" size="small">当前客户</Text>
              <div>
                <Text strong>{selected.name || `#${selected.id}`}</Text>
                {selected.star != null && (
                  <Text type="warning" size="small" style={{ marginLeft: sp.xs }}>
                    {selected.star}★
                  </Text>
                )}
              </div>
            </div>
            {!wide && (
              <Button
                theme="borderless"
                icon={<IconUserCircle />}
                onClick={() => setProfileOpen(true)}
              >
                档案
              </Button>
            )}
          </div>
        )}
      </div>

      <div style={mainRow}>
        <div style={sidebarWrap}>
          <CustomerListPanel
            selectedId={selected?.id ?? null}
            onSelect={(c) => setSelected(c)}
          />
        </div>

        {wide && (
          <div style={{ width: 260, flexShrink: 0, ...profileCard }}>
            <CustomerProfilePanel
              leadId={selected?.id ?? null}
              leadName={selected?.name}
              leadIntention={selected?.intentionLevel}
              leadStatus={selected?.status}
              leadStar={selected?.star ?? null}
              leadScore={selected?.score ?? null}
              leadNo={selected?.leadNo}
              onPickTemplate={quickSend}
            />
          </div>
        )}

        <div style={{ flex: 1, minWidth: 0, display: "flex" }}>
          <ChatPanel
            ref={chatRef}
            leadId={selected?.id ?? null}
            leadName={selected?.name}
            leadIntention={selected?.intentionLevel}
            leadStatus={selected?.status}
            leadStar={selected?.star ?? null}
            leadScore={selected?.score ?? null}
          />
        </div>
      </div>

      <SideSheet
        title="客户档案"
        visible={profileOpen}
        onCancel={() => setProfileOpen(false)}
        width={360}
        placement="right"
        bodyStyle={{ padding: 0 }}
      >
        <CustomerProfilePanel
          leadId={selected?.id ?? null}
          leadName={selected?.name}
          leadIntention={selected?.intentionLevel}
          leadStatus={selected?.status}
          leadStar={selected?.star ?? null}
          leadScore={selected?.score ?? null}
          leadNo={selected?.leadNo}
          onPickTemplate={quickSend}
        />
      </SideSheet>
    </div>
  );
};

export default AiCoachChatPage;
